import { findImageCountsRule, parsePageValidation } from "@patient-watch/forms/pageValidation";
import type { EntryValueViewT, PageViewT } from "@patient-watch/types";
import type { CarouselImage } from "@/features/blocks/utils/parseCarouselImages";
import { pairImageCounts } from "@/features/pages/utils/imageCounts";

export interface ImageCountRowT {
    entryValue: EntryValueViewT;
    image: CarouselImage;
}

/** One `image_counts` page: each picture with the answer given for it, in the order of the pictures. */
export interface ImageCountGroupT {
    key: string;
    /** The sum the counts must reach, from the page rule. */
    total: number | null;
    rows: ImageCountRowT[];
}

/**
 * Finds the answers that belong to a page's image-and-count layout, so they can be shown with their
 * pictures rather than as loose numbers. Only pages whose rule asks for `image_counts` are grouped, and
 * a page only contributes when at least one of its pictures has an answer.
 */
export function buildImageCountGroups(
    entryValues: readonly EntryValueViewT[],
    pages: readonly PageViewT[] | null | undefined
): ImageCountGroupT[] {
    if (!pages?.length) return [];

    const groups: ImageCountGroupT[] = [];
    for (const page of pages) {
        const rule = findImageCountsRule(parsePageValidation(page.page_validation));
        const layout = pairImageCounts(rule, page.inputs ?? [], page.blocks ?? []);
        if (!layout || !rule) continue;

        const rows = layout.rows.flatMap(({ input, image }) => {
            const entryValue = entryValues.find((ev) => ev.input_id === input.input_id);
            return entryValue ? [{ entryValue, image }] : [];
        });
        if (rows.length === 0) continue;

        groups.push({ key: layout.carouselBlockId, total: rule.total, rows });
    }
    return groups;
}
